import type { UserProfile, UserProfileView } from './types';

export function unwrapUserProfile(response: unknown): UserProfile {
  if (response && typeof response === 'object' && 'data' in response) {
    const envelope = response as { data?: UserProfile };
    if (envelope.data && typeof envelope.data === 'object') {
      return envelope.data;
    }
  }
  return response as UserProfile;
}

export function toUserProfileView(profile: UserProfile | null): UserProfileView | null {
  if (!profile) {
    return null;
  }

  const fromNames = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    id: profile.id,
    displayName: profile.name || fromNames || profile.email || '',
    avatarUrl: profile.imageUrl,
    email: profile.email,
    gender: profile.gender ?? null,
  };
}

export function isMaleGender(gender: string | null | undefined): boolean {
  const normalized = (gender ?? '').trim().toLowerCase();
  return normalized === 'man' || normalized === 'male';
}

/** Prefer uploaded picture; otherwise man/woman default. Missing data → woman. */
export function resolveProfilePictureSrc(
  picture: string | null | undefined,
  gender: string | null | undefined,
  defaults: { man: string; woman: string },
): string {
  if (picture) {
    return picture;
  }
  return isMaleGender(gender) ? defaults.man : defaults.woman;
}

export function formatLocalClock(now: Date = new Date()): string {
  const time = now.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${timeZone} (${time} UTC${sign}${hours}:${minutes})`;
}
